import { defineStore } from 'pinia';
import cache from 'src/utils/cache';
import type { dynamicObj } from 'src/pages/assets';
import { userApiService, authApiService } from 'src/services';
import { setWithExpiry, getWithExpiry } from 'src/utils/cacheHelper';
import { logger } from 'src/utils/logger';
import { useUserStore } from 'src/store/main';

import { subscriptionUsageRequest } from 'src/types';
import type {
  FeatureSubscription,
  FeatureSubscriptionCreateRequest,
} from 'src/sdk/auth/models/featureSubscription';
import { ApiExceptions } from 'src/exceptions';
import {
  docnet_setting,
  grouping_dashboard_setting,
  bod_connection_stream_setting,
  doc_operator_setting,
  open_search_enabled,
  CONSTANTS,
} from 'src/constants';
import { i18n } from 'src/boot/i18n';
import { notify } from 'src/components/notifications/notify';

const sub_app_key = 'sub_app';

export const useSubscriptionStore = defineStore('subscription', {
  state: () => ({
    usage: {} as dynamicObj,
    _usageCacheTime: 0,
    _usageCacheKey: '' as string,
    remaining_days: 0 as number,
    to_show_alert_days: 30,
    subscriptions: {} as dynamicObj,
    ready_to_expire_subscriptions: [] as dynamicObj[],
    subscriptionWindowClosed: true,
    loading: false,
    activated: false,
    settings: {} as dynamicObj,
    show_popup: false,
    feature_subscriptions: [] as FeatureSubscription[],
    feature_subscriptions_loaded: false,
    featureSubscriptionsPromiseResolve: null as (() => void) | null,
    featureSubscriptionsReqPromise: null as Promise<void> | null,
  }),
  getters: {
    whenToShowAlertDays(state) {
      return state.to_show_alert_days;
    },

    get_subscribed_apps(state) {
      const app_list = [''];
      app_list.length = 0;
      for (const [key, subsctiption] of Object.entries(state.subscriptions)) {
        if (subsctiption.days < 0) {
          continue;
        }

        for (const app of subsctiption.apps) {
          if (app_list.indexOf(app.app_key) == -1) {
            app_list.push(app.app_key);
          }
        }
      }
      return app_list;
    },

    subsciptionExpired(state) {
      let isExpired = true;

      for (const [key, subsctiption] of Object.entries(state.subscriptions)) {
        if (subsctiption.days < 0) {
          continue;
        }

        for (const app of subsctiption.apps) {
          if (app.app_key !== 'FELLOW_DOC2') {
            continue;
          }

          isExpired = false;
          break;
        }

        if (!isExpired) break;
      }

      return isExpired;
    },

    doc2AppUsage(state) {
      let usage = {};
      let tokens = 0;
      for (const [key, value] of Object.entries(state.usage)) {
        if (value.app_name !== 'DocBits') {
          continue;
        }
        usage = value;
        if (value.credit_used !== null) {
          tokens = tokens + Number(value.credit_used);
        } else {
          const result = tokens + Number(value.extraction_credit_used);
          tokens = result;
        }
      }
      return { tokens_used: tokens, usage: usage };
    },

    docFlowUsage(state) {
      let usage = {};
      let tokens = 0;
      for (const [key, value] of Object.entries(state.usage)) {
        if (value.app_name !== 'DocFlow') {
          continue;
        }
        usage = value;
        if (value.credit_used !== null) {
          tokens = tokens + Number(value.credit_used);
        } else {
          const result = tokens + Number(value.extraction_credit_used);
          tokens = result;
        }
      }
      return { tokens_used: tokens, usage: usage };
    },

    docFlowAdvancedUsage(state) {
      let usage = {};
      let tokens = 0;
      for (const [key, value] of Object.entries(state.usage)) {
        if (value.app_name !== 'Advanced Workflow') {
          continue;
        }
        usage = value;
        if (value.credit_used !== null) {
          tokens = tokens + Number(value.credit_used);
        } else {
          const result = tokens + Number(value.extraction_credit_used);
          tokens = result;
        }
      }
      return { tokens_used: tokens, usage: usage };
    },

    docNetUsage(state) {
      let usage = {};
      let tokens = 0;
      for (const [key, value] of Object.entries(state.usage)) {
        if (value.app_name !== 'DocNet') {
          continue;
        }
        usage = value;
        if (value.credit_used !== null) {
          tokens = tokens + Number(value.credit_used);
        } else {
          const result = tokens + Number(value.extraction_credit_used);
          tokens = result;
        }
      }
      return { tokens_used: tokens, usage: usage };
    },

    docSearchUsage(state) {
      let usage = {};
      let tokens = 0;
      for (const [key, value] of Object.entries(state.usage)) {
        if (value.app_name !== 'DocSearch') {
          continue;
        }
        usage = value;
        if (value.credit_used !== null) {
          tokens = tokens + Number(value.credit_used);
        } else {
          const result = tokens + Number(value.extraction_credit_used);
          tokens = result;
        }
      }
      return { tokens_used: tokens, usage: usage };
    },

    hasAppsSubscription(state) {
      let subsctiption_found = false;
      for (const [key, subsctiption] of Object.entries(state.subscriptions)) {
        if (subsctiption.days < 0) {
          continue;
        }

        for (const app of subsctiption.apps) {
          if (app.app_key !== 'FELLOW_APPS') {
            continue;
          }
          subsctiption_found = true;
          break;
        }

        if (subsctiption_found) break;
      }

      return subsctiption_found;
    },
  },
  actions: {
    async setSubscriptionInfo() {
      try {
        // Ensure user_id is available before attempting to fetch subscriptions
        const user_store = useUserStore();
        const user_id = user_store.getUserDetailByKey('id');

        if (!user_id || user_id === '') {
          console.log(
            '[subscription.store] user_id not available, fetching profile first...',
          );
          try {
            await user_store.getProfile();

            // ✅ FIX: Add retry logic to wait for user_id to become available
            let new_user_id = user_store.getUserDetailByKey('id');
            const maxRetries = 3;
            const retryInterval = 500; // milliseconds

            for (let i = 0; i < maxRetries && (!new_user_id || new_user_id === ''); i++) {
              console.log(
                `[subscription.store] Waiting for user_id (attempt ${i + 1}/${maxRetries})...`,
              );
              await new Promise(resolve => setTimeout(resolve, retryInterval));
              new_user_id = user_store.getUserDetailByKey('id');
            }

            if (!new_user_id || new_user_id === '') {
              console.warn(
                '[subscription.store] user_id still empty after retries, allowing access with limited subscription features',
              );
              // ✅ FIXED: Don't throw error, just mark and return false
              // User can still access dashboard without subscription info
              return false;
            }
            console.log(
              '[subscription.store] Profile loaded, user_id now available',
            );
          } catch (profileError) {
            console.error(
              '[subscription.store] Failed to load profile:',
              profileError,
            );
            // ✅ FIXED: Don't throw error, just mark and return false
            // Profile error should not block dashboard access
            return false;
          }
        }

        let subscriptions = this.getSubscriptionFromCache(sub_app_key);

        if (!subscriptions) {
          await this.getSubscriptionFromServer();
          subscriptions = this.getSubscriptionFromCache(sub_app_key);
        }

        this.processSubscriptions(subscriptions);
        return true;
      } catch (err: any) {
        // ✅ FIXED: Don't clear storage on subscription errors
        // Subscription is informational only and should NEVER cause a logout cascade
        console.error(
          '[subscription.store] Subscription fetch failed, allowing access with limited features:',
          err,
        );

        // Mark issue: Set empty subscription state to indicate failure
        this.subscriptions = {};
        this.ready_to_expire_subscriptions = [];

        // Return false to indicate subscription load failed, but user can continue
        return false;
      }
    },

    async saveSubscriptionChange(
      value: any,
      app_key: string,
      showNotification: any,
      updateSetting: any,
      showErrorToast: any,
      showSuccessToast: any,
    ): Promise<void> {
      let setting_type;

      switch (app_key) {
        case CONSTANTS.grouping_dashboard:
          setting_type = grouping_dashboard_setting;
          break;
        case CONSTANTS.bod_connection_stream:
          setting_type = bod_connection_stream_setting;
          break;
        case CONSTANTS.doc_net:
          setting_type = docnet_setting;
          break;
        case CONSTANTS.doc_operator:
          setting_type = doc_operator_setting;
          break;
        case CONSTANTS.opensearch:
          setting_type = open_search_enabled;
          break;
        case CONSTANTS.vertex_tax:
          setting_type = CONSTANTS.vertex_tax_integration_setting;
          break;

        default:
          break;
      }

      if (setting_type) {
        this.loading = true;
        if (value == true) {
          this.activated = true;
        } else {
          this.activated = false;
        }
        this.loading = false;
        await updateSetting(setting_type, this.activated);
        notify.success(i18n.t('save_successfully'));
      } else {
        notify.error(i18n.t('unknown_setting_type'));
      }
    },

    async getSubscriptionFromServer() {
      // CRITICAL FIX: Use AUTH SDK as source of truth for subscription data
      // AUTH SDK /v2/oauth2/profile already returns subscription in the profile response
      // No need to call separate /organisation/subscriptions endpoint
      try {
        const response = await userApiService.getSubscription();
        if (
          !response ||
          !response ||
          !Array.isArray(response) ||
          response.length == 0
        ) {
          return;
        }

        // Store subscription data
        const subscriptionData = response;
        setWithExpiry(sub_app_key, subscriptionData);
        logger.debug(
          '[Subscription] Successfully cached subscription from AUTH SDK',
        );
        return;
      } catch (err) {
        console.error('Subscription API Error:', err);
        throw new Error('network_error');
      } finally {
      }
    },

    processSubscriptions(subscription_data: any) {
      this.ready_to_expire_subscriptions = [] as dynamicObj[];

      // FIX: Distinguish between "API failed" (null/undefined) and "no subscriptions" (empty array)
      // Empty array [] is a valid response meaning "user has no active subscriptions"
      if (subscription_data === null || subscription_data === undefined) {
        // API call actually failed
        this.subscriptionNotFound();
        return;
      }

      for (const subscription of subscription_data) {
        this.setAboutToExpireSubscriptions(subscription);
        subscription.days = this.getRemainingDays(subscription);
      }

      this.subscriptions = subscription_data ?? ({} as dynamicObj);
    },

    subscriptionNotFound() {
      this.ready_to_expire_subscriptions = [] as dynamicObj[];
      this.ready_to_expire_subscriptions.push({
        days: -1,
        detail: {
          name: '',
        },
      });
      throw new Error('network_error');
    },

    setAboutToExpireSubscriptions(subscription: any) {
      for (const app of subscription.apps) {
        if (app.app_key !== 'FELLOW_DOC2') {
          continue;
        }
        const days = this.getRemainingDays(subscription);
        if (days < this.to_show_alert_days) {
          this.ready_to_expire_subscriptions.push({
            days: days,
            detail: subscription,
          });
        }
      }
    },

    getRemainingDays(subscription: dynamicObj): number {
      let days = -1;

      if (!subscription) {
        return days;
      }

      const end_date = this.getDateFromServerDate(subscription.end_date);
      const now = new Date().setHours(0, 0, 0, 0);
      days = this.datediff(now, end_date);
      return days;
    },

    getStartDate(subscription: dynamicObj): number {
      const start_date = this.getDateFromServerDate(subscription.start_date);
      return start_date;
    },

    getEndDate(subscription: dynamicObj): number {
      const end_date = this.getDateFromServerDate(subscription.end_date);
      return end_date;
    },

    datediff(now: number, end_date: number) {
      return Math.round((end_date - now) / (1000 * 60 * 60 * 24));
    },

    getDateFromServerDate(date: string) {
      const only_date = date.split(' ');
      return new Date(only_date[0]).setHours(0, 0, 0, 0);
    },

    subscriptionAlertShowDays() {
      return 30;
    },

    getSubscription() {
      return this.getSubscriptionFromCache(sub_app_key);
    },

    getSubscriptionFromCache(sub_app_key: string) {
      const subscriptions = getWithExpiry(sub_app_key) as dynamicObj;

      if (!subscriptions) {
        return null;
      }
       
      return subscriptions;
    },

    async getUsage(data: subscriptionUsageRequest) {
      // Cache usage stats for 5 minutes — auth service takes 3s per call.
      // Key on the full request so any field change (range, group_type,
      // filter_for_idp) refetches instead of serving a stale entry.
      const CACHE_TTL = 5 * 60 * 1000;
      const cacheKey = JSON.stringify(data);
      const now = Date.now();
      if (
        this._usageCacheKey === cacheKey &&
        this._usageCacheTime &&
        now - this._usageCacheTime < CACHE_TTL &&
        this.usage &&
        Object.keys(this.usage).length > 0
      ) {
        return;
      }

      this.usage = {};
      const response = await userApiService.getSubscriptionUsage(data);
      if (!response || !Array.isArray(response) || response.length === 0) {
        return;
      }
      this.usage = response;
      this._usageCacheKey = cacheKey;
      this._usageCacheTime = Date.now();
    },

    // Feature subscription lifecycle actions

    async fetchFeatureSubscriptions() {
      if (this.feature_subscriptions_loaded) {
        logger.info('[SUBSCRIPTION] Feature subscriptions already loaded - skipping redundant fetch');
        return;
      }

      if (this.featureSubscriptionsReqPromise) {
        return this.featureSubscriptionsReqPromise;
      }

      this.featureSubscriptionsReqPromise = new Promise<void>((resolve) => {
        this.featureSubscriptionsPromiseResolve = resolve;
        this._loadFeatureSubscriptions().catch((err) => {
          logger.warn('[SUBSCRIPTION] Unexpected error in fetchFeatureSubscriptions():', err);
        });
      });

      return this.featureSubscriptionsReqPromise;
    },

    // Internal: do not call directly. Use fetchFeatureSubscriptions() so loaded/in-flight state stays consistent.
    async _loadFeatureSubscriptions() {
      try {
        const response = await authApiService.listFeatureSubscriptions();
        // Backend wraps some responses as `{ success, data: [...] }`; unwrap
        // defensively so callers can rely on `.find()` / `.some()`.
        this.feature_subscriptions = Array.isArray(response)
          ? response
          : ((response as any)?.data ?? []);
        this.feature_subscriptions_loaded = true;
      } catch (err) {
        const msg = ApiExceptions.getErrorMessage(err as dynamicObj);
        notify.error(msg);
      } finally {
        if (this.featureSubscriptionsPromiseResolve) {
          this.featureSubscriptionsPromiseResolve();
        }
        this.featureSubscriptionsPromiseResolve = null;
        this.featureSubscriptionsReqPromise = null;
      }
    },

    async createFeatureSubscription(data: FeatureSubscriptionCreateRequest) {
      try {
        const result = await authApiService.createFeatureSubscription(data);
        this.feature_subscriptions_loaded = false;
        await this.fetchFeatureSubscriptions();
        notify.success(i18n.t('save_successfully'));
        return result;
      } catch (err) {
        const msg = ApiExceptions.getErrorMessage(err as dynamicObj);
        notify.error(msg);
        return null;
      }
    },

    async cancelFeatureSubscription(
      subId: string,
      options: { silent?: boolean } = {},
    ) {
      try {
        const result = await authApiService.cancelFeatureSubscription(subId);
        this.feature_subscriptions_loaded = false;
        await this.fetchFeatureSubscriptions();
        if (!options.silent) {
          notify.success(i18n.t('save_successfully'));
        }
        return result;
      } catch (err) {
        const msg = ApiExceptions.getErrorMessage(err as dynamicObj);
        if (!options.silent) {
          notify.error(msg);
        }
        return null;
      }
    },

    async reactivateFeatureSubscription(subId: string) {
      try {
        const result =
          await authApiService.reactivateFeatureSubscription(subId);
        this.feature_subscriptions_loaded = false;
        await this.fetchFeatureSubscriptions();
        notify.success(i18n.t('save_successfully'));
        return result;
      } catch (err) {
        const msg = ApiExceptions.getErrorMessage(err as dynamicObj);
        notify.error(msg);
        return null;
      }
    },

  },
});
